/**
 * POS Sync — outbound one-way Portal → POS.
 *
 * Two sections:
 *   1. Configuration card — SuperAdmin-only edit, admins see read-only.
 *      Webhook URL + auto-generated HMAC secret (last-4 visible) +
 *      include-PIN / include-role toggles + Save + Test webhook.
 *   2. Recent sync log — last 50 push attempts with status pill (SENT /
 *      PENDING / FAILED / DEAD), HTTP status, event type, and the user
 *      affected.
 *
 * SuperAdmin also gets a "Sync all employees now" button that triggers a
 * full reconcile.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowRightLeft, RefreshCw, Send, ShieldCheck, AlertTriangle, RotateCw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  SENT: "bg-emerald-100 text-emerald-800 border-emerald-200",
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  FAILED: "bg-orange-100 text-orange-800 border-orange-200",
  DEAD: "bg-red-100 text-red-800 border-red-200",
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function PosSyncSettings() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPERADMIN";

  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    webhook_url: "",
    secret: "",
    auth_header: "",
    include_pin: true,
    include_role: true,
  });
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, l] = await Promise.all([
          api.get("/admin/pos-sync/config"),
          api.get("/admin/pos-sync/log?limit=50"),
        ]);
        if (cancelled) return;
        setCfg(c.data.config);
        setForm({
          enabled: !!c.data.config.enabled,
          webhook_url: c.data.config.webhook_url || "",
          secret: "",
          auth_header: "",
          include_pin: !!c.data.config.include_pin,
          include_role: !!c.data.config.include_role,
        });
        setLog(l.data.log || []);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Failed to load POS sync config");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reload = async () => {
    try {
      const [c, l] = await Promise.all([
        api.get("/admin/pos-sync/config"),
        api.get("/admin/pos-sync/log?limit=50"),
      ]);
      setCfg(c.data.config);
      setLog(l.data.log || []);
    } catch (err) {
      toast.error("Failed to refresh POS sync state");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/pos-sync/config", form);
      toast.success("POS sync configuration saved");
      if (data.secret_reveal) setRevealedSecret(data.secret_reveal);
      setForm({ ...form, secret: "", auth_header: "" });
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const testWebhook = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pos-sync/test");
      if (data.result?.ok) {
        toast.success(`Test webhook OK · HTTP ${data.result.status_code}`);
      } else {
        toast.error(`Test webhook failed: ${data.result?.error || `HTTP ${data.result?.status_code}`}`);
      }
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  const reconcile = async () => {
    if (!window.confirm("Push every active employee to the POS now? Each row creates a sync log entry.")) return;
    setReconciling(true);
    try {
      const { data } = await api.post("/admin/pos-sync/reconcile");
      toast.success(`Pushed ${data.pushed} of ${data.total} employees`);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  if (!cfg) return <div className="text-sm text-gray-400">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="pos-sync-tab">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#C9A84C]/15"><ArrowRightLeft className="h-5 w-5 text-[#C9A84C]" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Outbound integration</div>
                <h3 className="font-heading text-lg font-bold text-[#1A2744]">POS Sync — Portal → POS</h3>
                <p className="text-xs text-gray-500 mt-1 max-w-md">
                  Pushes every employee create / update / deactivate / PIN-reset to your POS as an
                  HMAC-signed webhook. One-way only — the portal stays the source of truth.
                </p>
              </div>
            </div>
            {isSuperAdmin && (
              <Button
                variant="outline"
                onClick={reconcile}
                disabled={reconciling || !cfg.enabled}
                data-testid="pos-sync-reconcile"
              >
                <RotateCw className={`h-4 w-4 mr-2 ${reconciling ? "animate-spin" : ""}`} />
                {reconciling ? "Pushing…" : "Sync all employees now"}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pos-url">Webhook URL</Label>
              <Input
                id="pos-url"
                data-testid="pos-sync-url"
                value={form.webhook_url}
                onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                placeholder="https://your-pos.example.com/webhooks/clc"
                disabled={!isSuperAdmin || busy}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Endpoint receiving HMAC-SHA256-signed POSTs. Verify each request with the secret below.
              </p>
            </div>
            <div>
              <Label htmlFor="pos-secret">
                Shared secret{" "}
                {cfg.has_secret && (
                  <span className="text-[10px] text-gray-500 font-mono ml-1">
                    (current: ••••{cfg.secret_last4})
                  </span>
                )}
              </Label>
              <Input
                id="pos-secret"
                data-testid="pos-sync-secret"
                value={form.secret}
                onChange={(e) => setForm({ ...form, secret: e.target.value })}
                placeholder={cfg.has_secret ? "Leave blank to keep current secret" : "Auto-generated on save"}
                type="password"
                disabled={!isSuperAdmin || busy}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Used as the HMAC-SHA256 key for the <code>X-CLC-Signature</code> header.
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="pos-auth-header">
              Receiver Authorization header{" "}
              {cfg.has_auth_header && (
                <span className="text-[10px] text-gray-500 font-mono ml-1">
                  (current: ••••{cfg.auth_header_last4})
                </span>
              )}
              <span className="text-[10px] text-gray-400 ml-1">optional</span>
            </Label>
            <Input
              id="pos-auth-header"
              data-testid="pos-sync-auth-header"
              value={form.auth_header}
              onChange={(e) => setForm({ ...form, auth_header: e.target.value })}
              placeholder={cfg.has_auth_header
                ? "Leave blank to keep current value · type to replace · single space to clear"
                : "e.g. Bearer eyJhbGci…  or  Token abc123  (paste verbatim — we forward as-is)"}
              type="password"
              disabled={!isSuperAdmin || busy}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Pasted verbatim into the <code>Authorization</code> header on every outbound POST. Use this
              when the receiver lives behind GCP Cloud Run / API-gateway IAM and would otherwise return
              <strong> HTTP 403</strong>. The vendor tells you what to paste — including the prefix.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
              <div>
                <div className="text-sm font-semibold text-[#1A2744]">Sync enabled</div>
                <div className="text-[11px] text-gray-500">Master switch — disable to pause all pushes</div>
              </div>
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                disabled={!isSuperAdmin || busy}
                data-testid="pos-sync-enabled"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
              <div>
                <div className="text-sm font-semibold text-[#1A2744]">Include PIN</div>
                <div className="text-[11px] text-gray-500">Only emitted on CREATE / PIN_RESET</div>
              </div>
              <Switch
                checked={form.include_pin}
                onCheckedChange={(v) => setForm({ ...form, include_pin: v })}
                disabled={!isSuperAdmin || busy}
                data-testid="pos-sync-include-pin"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
              <div>
                <div className="text-sm font-semibold text-[#1A2744]">Include role</div>
                <div className="text-[11px] text-gray-500">Sends EMPLOYEE / MANAGER etc.</div>
              </div>
              <Switch
                checked={form.include_role}
                onCheckedChange={(v) => setForm({ ...form, include_role: v })}
                disabled={!isSuperAdmin || busy}
                data-testid="pos-sync-include-role"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end pt-2">
            <Button
              variant="outline"
              onClick={testWebhook}
              disabled={!cfg.has_secret || !cfg.webhook_url || busy}
              data-testid="pos-sync-test"
            >
              <Send className="h-4 w-4 mr-1" /> Send test event
            </Button>
            {isSuperAdmin && (
              <Button
                onClick={save}
                disabled={busy}
                className="bg-[#1A2744] hover:bg-[#0F1A30]"
                data-testid="pos-sync-save"
              >
                {busy ? "Saving…" : "Save configuration"}
              </Button>
            )}
          </div>

          {revealedSecret && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs">
              <div className="font-semibold text-emerald-800 mb-1 flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" /> New secret saved — copy it now
              </div>
              <code className="block bg-emerald-900 text-emerald-100 p-2 rounded font-mono text-[11px] break-all" data-testid="pos-sync-secret-reveal">
                {revealedSecret}
              </code>
              <p className="text-[11px] text-emerald-700 mt-1">
                Add this to your POS receiver. We won&apos;t show it again — only the last 4 characters remain visible.
              </p>
            </div>
          )}

          {cfg.last_reconcile_at && (
            <div className="text-[11px] text-gray-500 pt-1">
              Last full reconcile: <strong>{fmt(cfg.last_reconcile_at)}</strong>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Recent activity</div>
              <h4 className="font-heading text-base font-bold text-[#1A2744]">POS sync log</h4>
            </div>
            <Button size="sm" variant="ghost" onClick={reload} data-testid="pos-sync-refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>HTTP</TableHead>
                <TableHead>Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {log.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-8">
                  No sync events yet. Enable the integration above and create or edit a user to trigger one.
                </TableCell></TableRow>
              )}
              {log.map((r) => (
                <TableRow key={r.id} data-testid={`pos-sync-log-${r.id}`}>
                  <TableCell className="text-xs text-gray-500 whitespace-nowrap">{fmt(r.attempt_at || r.created_at)}</TableCell>
                  <TableCell className="text-xs font-mono">{r.event}</TableCell>
                  <TableCell className="text-xs">{r.employee_code || "—"}</TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_STYLES[r.status] || "bg-gray-100"} text-[10px]`}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono">{r.response_code ?? "—"}</TableCell>
                  <TableCell className="text-xs text-red-700 max-w-[220px] truncate" title={r.error || ""}>
                    {r.error || ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900 flex gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold">Receiver requirements</p>
          <p>
            Your POS endpoint must verify the <code>X-CLC-Signature</code> header
            (<code>sha256=&lt;hex&gt;</code>) as an HMAC-SHA256 over the raw request body using the shared
            secret. Failed deliveries auto-retry every minute up to 3 times before being marked DEAD.
          </p>
        </div>
      </div>
    </div>
  );
}
